import { type HttpErrorResponse } from '@angular/common/http';

/**
 * @example
 * ```json
 * {
 *   "headers": {
 *     "lazyUpdate": null
 *     "normalizedNames": {},
 *   },
 *   "status": 428,
 *   "statusText": "OK",
 *   "url": "https://finnair.com/d/api-url",
 *   "ok": false,
 *   "name": "HttpErrorResponse",
 *   "message": "Http failure response for https://www.finnair.com/d/api-url: 428 OK",
 *   "error": {
 *     "branding_cust_url": "https://www.finnair.com/challenge/challenge.html",
 *     "branding_type": "custom_branding",
 *     "chlg_duration": 30,
 *     "count": 1,
 *     "cpu": false,
 *     "difficulty": 15000,
 *     "nonce": "5452a5ea9204ab279d57",
 *     "provider": "crypto",
 *     "sec-cp-challenge": "true",
 *     "timeout": 1000,
 *     "timestamp": 1715158515,
 *     "token": "AAQAAAAJ_____"
 *   }
 * }
 * ```
 */
export interface AkamaiCryptoChallengeErrorResponse extends HttpErrorResponse {
  error: {
    branding_cust_url: string;
    branding_type: 'custom_branding';
    chlg_duration: number;
    count: number;
    cpu: false;
    difficulty: number;
    nonce: string;
    provider: 'crypto';
    'sec-cp-challenge': string;
    timeout: number;
    timestamp: number;
    token: string;
  };
}

export interface SpecialAssistanceResponseData {
  caseId: string;
  caseNumber: string;
}

export interface RefundRequestResponseData {
  status: string;
}

export interface PortalServicesMailerResponseData {
  returnValue: string;
}

export interface CancellationCoverClaimResponseData {
  returnValue: string;
}

export interface CertificatesForInsuranceCompanyResponseData {
  message: string;
}

export interface FinnairPlusContactResponseData {
  returnValue: string;
}

export interface ClaimMissingPartnerPointsResponseData {
  returnValue: string;
}

export interface ClaimMissingAviosResponseData {
  returnValue: string;
}

export interface LoggedInMissingPointsResponseData {
  errorCode?: LoggedInMissingPointsErrorCode;
  errorMessage?: string;
}

// Keys copied from current state of the back-end side
export enum LoggedInMissingPointsErrorCode {
  // GENERIC
  INTERNAL_ERROR = 'INTERNAL ERROR',
  ADN_PROXY_INTERNAL_ERROR = 'ADN PROXY INTERNAL ERROR',
  COUPON_NOT_MATCHING = 'COUPON NOT MATCHING',
  THIS_FREQUENT_FLYER_CARD_IS_NOT_RECOGNIZED = 'THIS FREQUENT FLYER CARD IS NOT RECOGNIZED',
  THIS_FREQUENT_FLYER_CARD_AND_PROGRAM_ARE_NOT_RECOGNIZED = 'THIS FREQUENT FLYER CARD AND PROGRAM ARE NOT RECOGNIZED',
  THIS_FREQUENT_FLYER_CARD_IS_NOT_RECOGNIZED_BUT_PROGRAM_CODE_IS_AY = 'THIS FREQUENT FLYER CARD IS NOT RECOGNIZED BUT PROGRAM CODE IS AY',
  PASSENGER_NOT_FOUND_FOR_THE_FLIGHT_NUMBER_ON_THE_REQUIRED_DATE = 'PASSENGER NOT FOUND FOR THE FLIGHT NUMBER ON THE REQUIRED DATE',
  CITY_PAIR_NOT_FOUND = 'CITY PAIR NOT FOUND',
  TICKET_NUMBER_NOT_FOUND = 'TICKET NUMBER NOT FOUND',
  BOOKING_CLASS_NOT_MATCHING = 'BOOKING CLASS NOT MATCHING',
  CABIN_CODE_NOT_MATCHING = 'CABIN CODE NOT MATCHING',
  ACCRUAL_PROCESS_HAS_BEEN_CONFIRMED_EARLIER = 'ACCRUAL PROCESS HAS BEEN CONFIRMED EARLIER',
  FLIGHT_IS_NOT_FOUND_FOR_THIS_CITY_PAIR_ON_THE_MENTIONED_DATE = '[Given flight] IS NOT FOUND FOR THIS CITY PAIR ON THE MENTIONED DATE',
  NO_FLIGHT_FOUND_FOR_THIS_CITY_PAIR_ON_THE_MENTIONED_DATE = 'NO FLIGHT FOUND FOR THIS CITY PAIR ON THE MENTIONED DATE',
  INVALID_PARAMETER_IN_QUERY = 'INVALID PARAMETER IN QUERY',
  FREQUENT_FLYER_PROGRAM_INVALID_FOR_CHARTER_FLIGHT = 'FREQUENT FLYER PROGRAM INVALID FOR CHARTER FLIGHT',
  FREQUENT_FLYER_PROGRAM_INVALID = 'FREQUENT FLYER PROGRAM INVALID',
  // SPECIFIC
  PASSENGER_IS_NO_SHOW_FOR_THIS_FLIGHT = 'PASSENGER IS NO SHOW FOR THIS FLIGHT/DATE',
  PASSENGER_DID_NOT_FLY_ON_THIS_FLIGHT = 'PASSENGER DID NOT FLY ON THIS FLIGHT/DATE',
  PASSENGER_IS_AN_ID_PASSENGER = 'PASSENGER IS AN ID PASSENGER',
  THIS_FLIGHT_IS_AN_ADHOC_FLIGHT = 'THIS FLIGHT IS AN ADHOC FLIGHT',
  THIS_FLIGHT_IS_A_CHARTER_FLIGHT = 'THIS FLIGHT IS A CHARTER FLIGHT',
  THIS_PASSENGER_USED_A_REDEMPTION_TICKET = 'THIS PASSENGER USED A REDEMPTION TICKET',
  THIS_FLIGHT_WAS_ALREADY_ACCRUED = 'THIS FLIGHT WAS ALREADY ACCRUED',
  THIS_FLIGHT_WAS_ALREADY_ACCRUED_FOR_ANOTHER_FREQUENT_FLYER_CARD = 'THIS FLIGHT WAS ALREADY ACCRUED FOR ANOTHER FREQUENT FLYER CARD',
  // eslint-disable-next-line quotes
  PASSENGERS_NAME_NOT_FOUND = `PASSENGER'S NAME NOT MATCHING`,
  MARKETING_CARRIER_IS_INVALID_FOR_LOYALTY = 'MARKETING CARRIER IS INVALID FOR LOYALTY',
  REQUEST_NOT_YET_ELIGIBLE = 'REQUEST NOT YET ELIGIBLE - PROCESSING DELAY NOT REACHED',
  REQUEST_NOT_ELIGIBLE_ANYMORE = 'REQUEST NOT ELIGIBLE ANYMORE',
}

export const localizationKeyGenericError = 'SERVICE_FORMS.serviceForms.missingPoints.error.genericError';

export const loggedInMissingPointsErrorCodeToLocalizationKeyMap: { [key in LoggedInMissingPointsErrorCode]?: string } =
  {
    // GENERIC - should default to the genericError key.
    // SPECIFIC
    [LoggedInMissingPointsErrorCode.PASSENGER_IS_NO_SHOW_FOR_THIS_FLIGHT]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.passengerIsNoShowForThisFlightDate',
    [LoggedInMissingPointsErrorCode.PASSENGER_DID_NOT_FLY_ON_THIS_FLIGHT]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.passengerDidNotFlyOnThisFlightDate',
    [LoggedInMissingPointsErrorCode.PASSENGER_IS_AN_ID_PASSENGER]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.passengerIsAnIdPassenger',
    [LoggedInMissingPointsErrorCode.THIS_FLIGHT_IS_AN_ADHOC_FLIGHT]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.thisFlightIsAnAdhocFlight',
    [LoggedInMissingPointsErrorCode.THIS_FLIGHT_IS_A_CHARTER_FLIGHT]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.thisFlightIsACharterFlight',
    [LoggedInMissingPointsErrorCode.THIS_PASSENGER_USED_A_REDEMPTION_TICKET]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.thisPassengerUsedARedemptionTicket',
    [LoggedInMissingPointsErrorCode.THIS_FLIGHT_WAS_ALREADY_ACCRUED]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.thisFlightWasAlreadyAccrued',
    [LoggedInMissingPointsErrorCode.THIS_FLIGHT_WAS_ALREADY_ACCRUED_FOR_ANOTHER_FREQUENT_FLYER_CARD]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.thisFlightWasAlreadyAccruedForAnotherFrequentFlyerCard',
    [LoggedInMissingPointsErrorCode.PASSENGERS_NAME_NOT_FOUND]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.passengerNameNotFound',
    [LoggedInMissingPointsErrorCode.MARKETING_CARRIER_IS_INVALID_FOR_LOYALTY]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.marketingCarrierIsInvalidForLoyalty',
    [LoggedInMissingPointsErrorCode.REQUEST_NOT_YET_ELIGIBLE]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.requestNotYetEligibleProcessingDelayNotReached',
    [LoggedInMissingPointsErrorCode.REQUEST_NOT_ELIGIBLE_ANYMORE]:
      'SERVICE_FORMS.serviceForms.missingPoints.error.requestNotEligibleAnymore',
  };
